/**
 * 서버 병행 i18n 키 렌더 프리미티브 (화면 무관)
 *
 * saas-ai-agent 는 표시 문자열(ko 원문)과 i18n 키를 함께 내린다.
 *   { name: "유가(WTI)", name_key: "bfFactorOilWtiLabel" }
 *   { judgment: "목표 충족", judgment_key: "mpAiJudgeTargetMet", judgment_args: {...} }
 *   { key: "unitCaseFmt", args: { n: 7 } }            // 수치 셀(value_i18n·kpis_i18n)
 * 키가 번역표에 있으면 그 값을, 없으면 ko 원문을 쓴다 — 번역이 빠져도 키 문자열이 노출되지 않는다.
 *
 * args 안에서 값이 다시 키인 인자는 `xxxKey`(+`xxxKeyArgs`) 규약으로 오고,
 * resolveTraceArgs 가 t() 로 풀어 `{{xxx}}` 자리에 넣는다(aiTraceArgs.js 와 같은 계약).
 */
import { keyedText, resolveTraceArgs } from "@plannel/components/ai/aiTraceArgs";

/** 키가 실제로 번역되면 그 값, 아니면 폴백 문자열(정의는 aiTraceArgs.keyedText 하나다). */
export const tKeyOr = keyedText;

/** obj 의 `{field}` / `{field}_key` / `{field}_args` 규약을 한 번에 읽는다. */
export function fieldText(t, obj, field) {
  if (!obj) return "";
  return tKeyOr(t, obj[`${field}_key`], obj[field], obj[`${field}_args`]);
}

/** `{key, args}` 형태의 수치 셀(value_i18n·kpis_i18n 항목). */
export function cellText(t, cell, fallback) {
  if (!cell || !cell.key) return fallback ?? "";
  return tKeyOr(t, cell.key, fallback, cell.args);
}

// ── 서버 오류 코드 → 사용자 문구 키 ─────────────────────────────────────────
// 서버는 상태·오류를 `error_code`(영문 상수)로 내리고, 사용자 문구는 프론트가 고른다.
// `error` 문자열은 "CODE: 상세" 꼴(상세는 한국어)이라 화면에 그대로 노출하지 않는다.
const ERROR_CODE_KEYS = {
  AI_SUGGEST_FAILED:           "aiSuggestFailed",
  AI_REPORT_FAILED:            "aiNarrativeUnavailable",
  AI_ISSUE_ANALYSIS_FAILED:    "aiNarrativeUnavailable",
  RP_POLICY_OPTIMIZE_FAILED:   "aiNarrativeUnavailable",
  ALL_SCENARIOS_FAILED:        "MSG_EngineRunFailed",
  ENGINE_NO_RESULT:            "MSG_EngineRunFailed",
  SCENARIO_SIM_FAILED:         "MSG_EngineRunFailed",
  ENGINE_TIMEOUT:              "MSG_EngineTimeout",
  NO_RP_BASE_VERSION:          "aiNoBaseVersion",
  NO_IP_BASE_VERSION:          "aiNoBaseVersion",
  NO_OPEN_DP_VERSION:          "aiNoBaseVersion",
  BF_BASE_VERSION_MISSING:     "aiNoBaseVersion",
  BF_SCENARIO_VERSION_MISSING: "aiNoBaseVersion",
  REQUEST_NOT_FOUND:           "aiRequestNotFound",
  REQUEST_NOT_COMPLETED:       "aiRequestNotFound",
  SCENARIO_NOT_FOUND:          "aiRequestNotFound",
  PLAN_VER_MISSING:            "aiRequestNotFound",
  ENTRY_NOT_FOUND:             "aiRequestNotFound",
};
// 모듈 코드가 이름에 끼는 계열(NO_RELEASED_DP_VERSION 등)은 패턴으로 받는다.
const ERROR_CODE_PATTERNS = [[/^NO_RELEASED_[A-Z0-9_]*VERSION$/, "aiNoBaseVersion"]];

/** 응답에서 오류 코드만 뽑는다 — `error_code` 가 정본, 없으면 `error` 의 "CODE:" 접두. */
export function errorCodeOf(result) {
  if (!result) return "";
  const code = String(result.error_code || "").trim();
  if (code) return code;
  const m = /^([A-Z][A-Z0-9_]{2,}):/.exec(String(result.error || "").trim());
  return m ? m[1] : "";
}

/**
 * 오류 표시 문구 — 코드(또는 서버가 지목한 `error_key`)를 화면 언어 문구로 바꾼다.
 * 코드가 없을 때만 서버 문자열을 쓴다(한국어 상세는 console 로만 남긴다).
 */
export function errorText(t, result, fallbackKey = "MSG_AnalysisFailed") {
  if (!t) return "";
  if (result?.error_key) return tKeyOr(t, result.error_key, t(fallbackKey));
  const code = errorCodeOf(result);
  const key = ERROR_CODE_KEYS[code]
    || ERROR_CODE_PATTERNS.find(([re]) => re.test(code))?.[1];
  if (key) return tKeyOr(t, key, t(fallbackKey));
  if (!code && result?.error) return String(result.error);
  return t(fallbackKey);
}

// ── 시나리오 표시 이름 ─────────────────────────────────────────────────────────
// 비교표·선택 목록·확정 카드가 같은 표기를 쓰도록 여기 한 곳에 둔다(모듈 파일도 import 한다).

// role → 시나리오 표시 번호 (base 는 "Base"). alt1→1, alt2→2, 그 외(누적추가)→idx.
export const ROLE_SCEN_NO = { alt1: 1, alt2: 2 };

// 역할 라벨 — "기준 시나리오" / "시나리오 N". t 가 없으면 한글 폴백(점진적 전환 하위호환).
export const scLabel = (sc, idx, t) => {
  if (sc?.role === "base") return t ? t("baseScenarioLabel") : "기준 시나리오";
  const n = ROLE_SCEN_NO[sc?.role] ?? idx;
  return t ? t("scenarioNumber", { n }) : `시나리오 ${n}`;
};

// 시나리오 표시명을 렌더 시점 언어로 재평가한다. 인스턴스에 굳어진 문자열 대신
// 병행 i18n 키(name_key + name_args)를 t()로 다시 풀어 언어 전환에 반응하게 한다.
// 우선순위: 사용자 편집(name_edited)→원문 리터럴; 아니면 name_key(+name_args)→t(); key 없으면 name 폴백.
// 번호 접미사(name_suffix)는 언어 중립("-2" 등)이므로 t() 결과 뒤에 그대로 조립한다.
export function dispName(sc, t) {
  if (!sc) return "";
  if (sc.name_edited) return sc.name ?? "";
  // AI 추천 변형: 적용 단위(name_scope_key) + 실제 적용 파라미터(name_segment_keys)를 각각
  // t()로 풀어 "{적용 단위} — {파라미터 + 파라미터}" 로 조립한다. name_key보다 우선.
  //   (고정 카탈로그 라벨의 과대광고·중복 이름 방지 — 카드가 실제로 바꾸는 것만 이름에 남긴다.)
  //   MP AI 추천 alt1 은 둘 다, IP 는 세그먼트만 보낸다. 한쪽만 있으면 그것만 쓴다.
  if (t && (sc.name_scope_key || (Array.isArray(sc.name_segment_keys) && sc.name_segment_keys.length))) {
    const seg = (sc.name_segment_keys || []).map(k => t(k)).join(" + ");
    const scope = sc.name_scope_key ? t(sc.name_scope_key) : "";
    const base = (scope && seg) ? `${scope} — ${seg}` : (scope || seg);
    if (base) return sc.name_suffix ? `${base}${sc.name_suffix}` : base;
  }
  if (!sc.name_key || !t) return sc.name ?? "";
  // name_args 는 resolveTraceArgs 를 지나야 한다 — 값이 다시 키인 인자(scopeKey·nameKey)가
  //   풀리지 않으면 aiVariantWithScope 의 {{name}}·{{scope}} 가 빈 채로 렌더된다.
  const base = t(sc.name_key, resolveTraceArgs(t, sc.name_args));
  return sc.name_suffix ? `${base}${sc.name_suffix}` : base;
}

// 선택 목록용 "역할 라벨 · 표시명" — 비교표 머리와 같은 표기.
export const scenarioPickLabel = (sc, idx, t) => {
  const name = dispName(sc, t);
  return name ? `${scLabel(sc, idx, t)} · ${name}` : scLabel(sc, idx, t);
};

export default fieldText;
